// /workspace — operator's cost + activity dashboard. Aggregates the workspace-
// wide picture so operators don't have to flip between /sources and detail
// pages to understand what's running.
//
// Sections (CC-style — dense, mono numbers, no marketing chrome):
//   1. Stats grid: sources, events 24h, active triggers, schedules, failing
//   2. Top sources (last 1h) — where the volume is right now
//   3. Action strip: workspace pause-all, refresh

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { pauseAllTriggersWorkspace, getOrphanStorage, cleanupOrphanStorage, listSecrets, createSecret, rotateSecret, revealSecret, deleteSecret, getSecretUsage } from '../api';
import type { OrphanStorage, SecretRedactedClient } from '../api';
import TopHeader from '../components/TopHeader';
import { useToast } from '../components/Toast';

interface WorkspaceStats {
  snapshot_at: string;
  sources: { active: number; archived: number; total: number };
  events_24h: number;
  triggers: { active: number; failing: number };
  schedules: { active: number; failing: number };
  top_sources_1h: Array<{ source_id: string; source_name: string; events_1h: number }>;
}

export default function WorkspacePage(): React.ReactElement {
  const toast = useToast();
  const [stats, setStats] = useState<WorkspaceStats | null>(null);
  const [orphan, setOrphan] = useState<OrphanStorage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  function reload(): void {
    setRefreshing(true); setError(null);
    Promise.all([
      fetch('/api/v1/workspace/stats').then(r => r.json()).then(s => setStats(s as WorkspaceStats)),
      getOrphanStorage().then(setOrphan).catch(() => setOrphan(null)),
    ]).catch(e => setError(String(e))).finally(() => setRefreshing(false));
  }

  useEffect(() => { reload(); }, []);

  async function handleCleanupOrphan(): Promise<void> {
    if (!orphan) return;
    const totalBQ = orphan.totals.bq_datasets + orphan.totals.bq_shared_tables;
    const totalGCS = orphan.totals.gcs_prefixes;
    const sizeMB = ((orphan.totals.bq_size_bytes + orphan.totals.gcs_size_bytes) / 1024 / 1024).toFixed(2);
    const ok = await toast.confirm({
      title: 'Wipe orphan storage?',
      message: `This drops ${totalBQ} BQ resource${totalBQ === 1 ? '' : 's'} and ${totalGCS} GCS prefix${totalGCS === 1 ? '' : 'es'} that don't belong to any source in this workspace (active OR archived).\n\nFreed: ~${sizeMB} MB.\n\nIRREVERSIBLE. Type WIPE to confirm.`,
      destructive: true,
      confirmLabel: 'Wipe orphan storage',
      requireType: 'WIPE',
    });
    if (!ok) return;
    setCleaning(true);
    try {
      const result = await cleanupOrphanStorage();
      const dropped = result.dropped.bq_datasets.length + result.dropped.bq_shared_tables.length + result.dropped.gcs_prefixes.length;
      toast.show(`Wiped ${dropped} resource${dropped === 1 ? '' : 's'}.${result.errors.length ? ` ${result.errors.length} error${result.errors.length === 1 ? '' : 's'}.` : ''}`, { kind: result.errors.length ? 'warning' : 'success' });
      reload();
    } catch (e) {
      toast.show(`Cleanup failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setCleaning(false);
    }
  }

  return (
    <div>
      <TopHeader env="Activity" />

      <div className="shell">
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18, gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--ink-3)', marginBottom: 4 }}>workspace</div>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Activity & cost</h1>
            {stats && (
              <div style={{ marginTop: 4, fontSize: 11.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>snapshot at {new Date(stats.snapshot_at).toLocaleString()}</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn sm ghost" onClick={reload} disabled={refreshing}>{refreshing ? '↻ refreshing…' : '↻ refresh'}</button>
            <button
              className="btn sm ghost"
              style={{ color: 'var(--danger)' }}
              onClick={async () => {
                const ok = await toast.confirm({
                  title: 'Pause every trigger in this workspace?',
                  message: 'Active triggers across every Source flip to paused. Ingestion keeps landing in storage; nothing fires downstream until you resume per-source or per-trigger.\n\nUse this only during incidents. Type PAUSE to confirm.',
                  destructive: true,
                  confirmLabel: 'Pause workspace',
                  requireType: 'PAUSE',
                });
                if (!ok) return;
                try {
                  const r = await pauseAllTriggersWorkspace();
                  toast.show(`Paused ${r.total_paused} trigger${r.total_paused === 1 ? '' : 's'} across ${r.per_source.length} source${r.per_source.length === 1 ? '' : 's'}.`, { kind: 'success' });
                  reload();
                } catch (e) { toast.show(`Pause-all failed: ${(e as Error).message}`, { kind: 'error' }); }
              }}
            >pause all triggers</button>
            <Link to="/sources" className="btn sm" style={{ textDecoration: 'none' }}>Sources →</Link>
          </div>
        </div>

        {error && <div style={{ padding: 14, background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 8, fontSize: 13, color: 'var(--danger)' }}>{error}</div>}
        {!stats && !error && <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>}

        {stats && (
          <>
            {/* Stat grid — five cards, mono numerals, dense. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
              <StatCard label="Sources" primary={stats.sources.active} suffix={`active · ${stats.sources.archived} archived`} />
              <StatCard label="Events" primary={stats.events_24h} suffix="last 24h" />
              <StatCard label="Triggers" primary={stats.triggers.active} suffix={`active${stats.triggers.failing > 0 ? ` · ${stats.triggers.failing} failing` : ''}`} alert={stats.triggers.failing > 0} />
              <StatCard label="Schedules" primary={stats.schedules.active} suffix={`active${stats.schedules.failing > 0 ? ` · ${stats.schedules.failing} failing` : ''}`} alert={stats.schedules.failing > 0} />
            </div>

            {/* Top sources by 1h activity. */}
            <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--line)', fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Top sources · last 1h</div>
              {stats.top_sources_1h.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>No events in the last hour.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {stats.top_sources_1h.map((s, i) => (
                      <tr key={s.source_id} style={i > 0 ? { borderTop: '1px solid var(--line)' } : {}}>
                        <td style={{ padding: '10px 16px' }}>
                          <Link to={`/sources/${s.source_id}`} style={{ color: 'var(--ink)', textDecoration: 'none', fontWeight: 500 }}>{s.source_name}</Link>
                          <span style={{ marginLeft: 8, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>{s.source_id}</span>
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 12.5, color: 'var(--ink-2)' }}>
                          {s.events_1h.toLocaleString()} events
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Orphan storage — anything in BQ + GCS that no source in this
                workspace owns. Operator drops legacy v1 / fully-deleted-source
                data in one click (with type-to-confirm). */}
            {orphan && (orphan.totals.bq_datasets + orphan.totals.bq_shared_tables + orphan.totals.gcs_prefixes > 0) && (
              <div style={{ marginTop: 20, background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 12, background: 'oklch(0.95 0.06 75 / 0.4)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Orphan storage</div>
                    <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>
                      Storage from sources that no longer exist in this workspace. Safe to drop —
                      no active or archived source uses it.
                    </div>
                  </div>
                  <button
                    className="btn sm"
                    style={{ background: 'var(--danger)', color: '#fff', border: 'none' }}
                    onClick={() => { void handleCleanupOrphan(); }}
                    disabled={cleaning}
                  >{cleaning ? 'wiping…' : 'wipe orphan storage'}</button>
                </div>
                <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--ink-2)' }}>
                  <div>
                    <div style={{ color: 'var(--ink-3)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>BQ datasets</div>
                    <div style={{ marginTop: 2, fontSize: 16, color: 'var(--ink)' }}>{orphan.totals.bq_datasets}</div>
                    {orphan.orphan_bq_datasets.slice(0, 5).map(d => (
                      <div key={d.dataset} style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{d.dataset} · {d.table_count} tbl</div>
                    ))}
                    {orphan.orphan_bq_datasets.length > 5 && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>… {orphan.orphan_bq_datasets.length - 5} more</div>}
                  </div>
                  <div>
                    <div style={{ color: 'var(--ink-3)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Shared tables</div>
                    <div style={{ marginTop: 2, fontSize: 16, color: 'var(--ink)' }}>{orphan.totals.bq_shared_tables}</div>
                    {orphan.orphan_bq_tables_in_shared.slice(0, 5).map(t => (
                      <div key={t.table} style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{t.table} · {t.row_count.toLocaleString()} rows</div>
                    ))}
                    {orphan.orphan_bq_tables_in_shared.length > 5 && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>… {orphan.orphan_bq_tables_in_shared.length - 5} more</div>}
                  </div>
                  <div>
                    <div style={{ color: 'var(--ink-3)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>GCS prefixes</div>
                    <div style={{ marginTop: 2, fontSize: 16, color: 'var(--ink)' }}>{orphan.totals.gcs_prefixes}</div>
                    {orphan.orphan_gcs_prefixes.slice(0, 5).map(p => (
                      <div key={p.prefix} style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{p.prefix} · {p.object_count} obj</div>
                    ))}
                    {orphan.orphan_gcs_prefixes.length > 5 && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>… {orphan.orphan_gcs_prefixes.length - 5} more</div>}
                  </div>
                  <div>
                    <div style={{ color: 'var(--ink-3)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total size</div>
                    <div style={{ marginTop: 2, fontSize: 16, color: 'var(--ink)' }}>
                      {((orphan.totals.bq_size_bytes + orphan.totals.gcs_size_bytes) / 1024 / 1024).toFixed(1)} MB
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>BQ {(orphan.totals.bq_size_bytes / 1024 / 1024).toFixed(1)} · GCS {(orphan.totals.gcs_size_bytes / 1024 / 1024).toFixed(1)} MB</div>
                  </div>
                </div>
              </div>
            )}

            {/* Secrets vault — workspace-scoped credentials referenced by
                triggers via @SECRET_NAME in URLs/headers. */}
            <SecretsPanel />

            {/* Cost hint strip. */}
            <div style={{ marginTop: 24, padding: '14px 16px', background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 8, fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.6 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Cost notes</div>
              Storage lifecycle: raw uploads expire after 30 days · batch blobs after 90.
              Per-event blobs (from <code style={{ fontFamily: 'var(--mono)' }}>/hook</code>) live indefinitely — operator-managed cleanup.
              BQ promote tables are partitioned by <code style={{ fontFamily: 'var(--mono)' }}>received_at</code> (DAY) and clustered on <code style={{ fontFamily: 'var(--mono)' }}>source_id</code>.
              No automatic table expiry — set per table in BQ console if needed.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SecretsPanel(): React.ReactElement {
  const toast = useToast();
  const [secrets, setSecrets] = useState<SecretRedactedClient[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newKind, setNewKind] = useState<SecretRedactedClient['kind']>('bearer');
  const [newValue, setNewValue] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [revealedFor, setRevealedFor] = useState<{ id: string; value: string } | null>(null);
  const [usage, setUsage] = useState<Record<string, number>>({});

  function reload(): void {
    listSecrets().then(rs => {
      setSecrets(rs);
      // Fan out usage fetches in parallel; non-blocking so the table renders
      // immediately and badges fill in.
      Promise.all(rs.map(s => getSecretUsage(s.secret_id).catch(() => ({ used_by: [] as Array<unknown> }))))
        .then(arr => {
          const map: Record<string, number> = {};
          rs.forEach((s, i) => { map[s.secret_id] = arr[i]?.used_by?.length ?? 0; });
          setUsage(map);
        });
    }).catch(e => setError(String(e)));
  }
  useEffect(() => { reload(); }, []);

  async function submitNew(): Promise<void> {
    if (!newName.trim() || !newValue.trim()) return;
    setSubmitting(true);
    try {
      const created = await createSecret({ name: newName.trim(), kind: newKind, value: newValue, description: newDescription.trim() || undefined });
      setRevealedFor({ id: created.secret_id, value: created.value });
      toast.show(`Secret "${created.name}" created. Copy the value now — it won't be shown again.`, { kind: 'success' });
      setNewName(''); setNewValue(''); setNewDescription('');
      setShowNew(false);
      reload();
    } catch (e) {
      toast.show(`Create failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRotate(s: SecretRedactedClient): Promise<void> {
    const value = window.prompt(`Rotate "${s.name}" — paste the new value:`);
    if (!value) return;
    try {
      await rotateSecret(s.secret_id, value);
      toast.show(`Secret "${s.name}" rotated. Old value stops resolving immediately.`, { kind: 'success' });
      reload();
    } catch (e) {
      toast.show(`Rotate failed: ${(e as Error).message}`, { kind: 'error' });
    }
  }

  async function handleReveal(s: SecretRedactedClient): Promise<void> {
    const ok = await toast.confirm({
      title: `Reveal "${s.name}"?`,
      message: 'The plaintext value will be shown briefly. Anyone with screen access can read it.',
      destructive: false,
      confirmLabel: 'Reveal',
    });
    if (!ok) return;
    try {
      const r = await revealSecret(s.secret_id);
      setRevealedFor({ id: s.secret_id, value: r.value });
    } catch (e) {
      toast.show(`Reveal failed: ${(e as Error).message}`, { kind: 'error' });
    }
  }

  async function handleDelete(s: SecretRedactedClient): Promise<void> {
    const ok = await toast.confirm({
      title: `Delete "${s.name}"?`,
      message: `Triggers that reference @${s.name} will start failing immediately. Cannot be undone.\n\nType the secret name to confirm.`,
      destructive: true,
      confirmLabel: 'Delete secret',
      requireType: s.name,
    });
    if (!ok) return;
    try {
      await deleteSecret(s.secret_id);
      toast.show(`Secret "${s.name}" deleted.`, { kind: 'success' });
      reload();
    } catch (e) {
      toast.show(`Delete failed: ${(e as Error).message}`, { kind: 'error' });
    }
  }

  return (
    <div style={{ marginTop: 20, background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Secrets vault</div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>
            Workspace-scoped credentials. Reference in trigger URLs / headers via <code style={{ fontFamily: 'var(--mono)' }}>@NAME</code>.
            The executor resolves at run time; values never round-trip to the operator after create.
          </div>
        </div>
        <button className="btn sm" onClick={() => setShowNew(true)}>+ New secret</button>
      </div>
      {error && <div style={{ padding: 12, color: 'var(--danger)', fontSize: 12.5 }}>{error}</div>}
      {secrets && secrets.length === 0 && (
        <div style={{ padding: 18, textAlign: 'center', color: 'var(--ink-3)', fontSize: 12.5 }}>
          No secrets stored. Click <strong>+ New secret</strong> to add one for use in <code style={{ fontFamily: 'var(--mono)' }}>enrich_fetch</code> headers or <code style={{ fontFamily: 'var(--mono)' }}>forward_webhook</code> URLs.
        </div>
      )}
      {secrets && secrets.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--bg-sunken)' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500, color: 'var(--ink-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>name · kind</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500, color: 'var(--ink-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>value</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500, color: 'var(--ink-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>used by</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500, color: 'var(--ink-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>last used</th>
              <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500, color: 'var(--ink-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>actions</th>
            </tr>
          </thead>
          <tbody>
            {secrets.map(s => (
              <tr key={s.secret_id} style={{ borderTop: '1px solid var(--line)' }}>
                <td style={{ padding: '8px 12px', fontFamily: 'var(--mono)', fontSize: 12 }}>
                  <span style={{ color: 'var(--accent)' }}>@{s.name}</span>
                  <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--ink-3)' }}>{s.kind}</span>
                  {s.description && <div style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--ui)', marginTop: 2 }}>{s.description}</div>}
                </td>
                <td style={{ padding: '8px 12px', fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink-2)' }}>
                  {revealedFor?.id === s.secret_id ? (
                    <span style={{ color: 'var(--ink)' }}>{revealedFor.value} <button className="btn sm ghost" style={{ marginLeft: 6, fontSize: 10 }} onClick={() => setRevealedFor(null)}>hide</button></span>
                  ) : (
                    s.value_redacted
                  )}
                </td>
                <td style={{ padding: '8px 12px', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>
                  {usage[s.secret_id] === undefined
                    ? '…'
                    : usage[s.secret_id] === 0
                      ? <span style={{ color: 'var(--ink-3)' }}>0 triggers</span>
                      : <span style={{ color: 'var(--ink-2)', fontWeight: 500 }}>{usage[s.secret_id]} trigger{usage[s.secret_id] === 1 ? '' : 's'}</span>}
                </td>
                <td style={{ padding: '8px 12px', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>
                  {s.last_used_at ? new Date(s.last_used_at).toLocaleString() : '—'}
                </td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                  <button className="btn sm ghost" onClick={() => { void handleReveal(s); }}>reveal</button>
                  <button className="btn sm ghost" onClick={() => { void handleRotate(s); }}>rotate</button>
                  <button className="btn sm ghost" onClick={() => { void handleDelete(s); }} style={{ color: 'var(--danger)' }}>delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showNew && (
        <div onClick={() => setShowNew(false)} style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'oklch(0.18 0.01 250 / 0.4)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 80 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 'min(540px, calc(100vw - 48px))', background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 12, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>New secret</div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 14, lineHeight: 1.5 }}>
              The plaintext value is returned ONCE on create. Copy it before closing this dialog;
              future GETs return the redacted form.
            </div>
            <div style={{ marginBottom: 4, fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Name</div>
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="ACME_API_KEY" style={{ width: '100%', boxSizing: 'border-box', padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 13, border: '1px solid var(--line)', borderRadius: 4, outline: 'none' }} />
            <div style={{ marginTop: 4, fontSize: 11, color: 'var(--ink-3)' }}>letter or _ to start, then letters/digits/underscore · max 60</div>
            <div style={{ marginTop: 12, marginBottom: 4, fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Kind</div>
            <select value={newKind} onChange={e => setNewKind(e.target.value as SecretRedactedClient['kind'])} style={{ width: '100%', boxSizing: 'border-box', padding: '6px 10px', fontSize: 13, border: '1px solid var(--line)', borderRadius: 4, outline: 'none' }}>
              <option value="bearer">bearer (Authorization: Bearer …)</option>
              <option value="api_key">api_key (X-API-Key, custom header, …)</option>
              <option value="basic">basic (Basic auth)</option>
              <option value="opaque">opaque (anything else)</option>
            </select>
            <div style={{ marginTop: 12, marginBottom: 4, fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Value</div>
            <input type="password" value={newValue} onChange={e => setNewValue(e.target.value)} placeholder="paste the credential" style={{ width: '100%', boxSizing: 'border-box', padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 13, border: '1px solid var(--line)', borderRadius: 4, outline: 'none' }} />
            <div style={{ marginTop: 12, marginBottom: 4, fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Description <span style={{ textTransform: 'none', color: 'var(--ink-3)' }}>· optional</span></div>
            <input value={newDescription} onChange={e => setNewDescription(e.target.value)} placeholder="What is this for?" style={{ width: '100%', boxSizing: 'border-box', padding: '6px 10px', fontSize: 13, border: '1px solid var(--line)', borderRadius: 4, outline: 'none' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn ghost" onClick={() => setShowNew(false)} disabled={submitting}>Cancel</button>
              <button className="btn primary" onClick={() => { void submitNew(); }} disabled={submitting || !newName.trim() || !newValue.trim()}>{submitting ? 'Creating…' : 'Create secret'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, primary, suffix, alert }: { label: string; primary: number; suffix: string; alert?: boolean }): React.ReactElement {
  return (
    <div style={{ padding: 16, background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 10 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontFamily: 'var(--mono)', fontWeight: 500, color: alert ? 'var(--danger)' : 'var(--ink)', lineHeight: 1 }}>{primary.toLocaleString()}</div>
      <div style={{ marginTop: 6, fontSize: 11.5, fontFamily: 'var(--mono)', color: alert ? 'var(--danger)' : 'var(--ink-3)' }}>{suffix}</div>
    </div>
  );
}
